import React, { useCallback } from 'react';
import c from 'classnames';

import useScreen from '@lyearn/core/hooks/useScreen';
import { CloseFill } from '@lyearn/icons';
import { Typography } from '@lyearn/ui';

import { HOTSPOT_RADIUS } from './HotspotContainer';
import { hotspotNumberText, hotspotStyle } from './style';
import { HotspotsRenderersProps, HotspotType } from './types';

const HotspotsRenderers: React.FC<HotspotsRenderersProps> = (props) => {
  const {
    hotspotsRef,
    setSelectedHotspot,
    width,
    height,
    hotspotsState,
    readOnly,
    selectedHotspot,
    removeOnClick,
    setHotspotsState,
  } = props;

  const { isMobile } = useScreen();

  const onDragStart = useCallback((event: React.DragEvent<HTMLDivElement>, index: number) => {
    event.dataTransfer.setData('draggedIndex', `${index}`);
    event.dataTransfer.effectAllowed = 'move';
    event.stopPropagation();
  }, []);

  const onHotspotClick = useCallback(
    (index: number) => {
      if (removeOnClick) {
        const newHotspots = [...hotspotsState];
        newHotspots.splice(index, 1);
        setHotspotsState(newHotspots);
        setSelectedHotspot(null);
        return;
      }

      if (index === selectedHotspot) {
        setSelectedHotspot(null);
      } else {
        setSelectedHotspot(index);
      }
    },
    [hotspotsState, removeOnClick, selectedHotspot, setHotspotsState, setSelectedHotspot],
  );

  if (!width || !height) {
    return null;
  }

  return (
    <>
      {hotspotsState.map((hotspot: HotspotType, index: number) => (
        <div key={index} className="hotspot-container" data-id={index}>
          {readOnly ? (
            <div
              className={c(
                'absolute flex h-16 w-16 items-center justify-center rounded-full bg-overlay-light opacity-100 group-hover:opacity-0',
                // Without hover state circle radius is 8px smaller than the hover state circle.
                // so we need to adjust the circle to have the same center.
                hotspotStyle(
                  height * (hotspot.y / 100) + HOTSPOT_RADIUS / 2,
                  width * (hotspot.x / 100) + HOTSPOT_RADIUS / 2,
                  false,
                ),
              )}>
              <div className="flex h-8 w-8 justify-center rounded-full bg-white shadow-04" />
            </div>
          ) : null}
          <div
            ref={(el) => (hotspotsRef.current![index] = el!)}
            className={c(
              'hotspot absolute flex cursor-pointer items-center justify-center rounded-full transition-all ease-in',
              readOnly && !isMobile ? 'opacity-0 group-hover:opacity-100' : '',
              index === selectedHotspot ? 'h-36 w-36' : 'h-32 w-32',
              hotspotStyle(
                height * (hotspot.y / 100),
                width * (hotspot.x / 100),
                index === selectedHotspot,
              ),
            )}
            draggable={readOnly ? false : true}
            onClick={() => onHotspotClick(index)}
            onDragStart={(event) => onDragStart(event, index)}>
            <div className="group/hotspot flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-04 transition ease-in">
              {removeOnClick ? (
                <CloseFill
                  className="hidden h-16 w-16 group-hover/hotspot:block"
                  fillColorToken="icon-error"
                />
              ) : null}
              <Typography
                as="div"
                className={c({ 'group-hover/hotspot:hidden': removeOnClick }, hotspotNumberText)}
                variant="body-short-03">
                {index + 1}
              </Typography>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default HotspotsRenderers;
